import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Building2, TrendingUp, Plus, ChevronLeft, ChevronRight, Calendar, 
  Search, Loader2, CheckCircle2, Wallet, RefreshCw, Clock, Check,
  MoreVertical, X, AlertCircle
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale, getMonthNames , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import { parseTurkishNumber } from "@/utils/numberUtils";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function TrainerCollectionsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "reports", "packages", "admin"]);
  const { formatPrice, currencySymbol } = useCurrency();

  const [pendingRents, setPendingRents] = useState({ pending_rents: [], total_pending: 0 });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedAccrual, setSelectedAccrual] = useState(null);

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "cash",
    payment_date: todayLocalString(),
    notes: ""
  });
  const [ignoreReason, setIgnoreReason] = useState("");
  
  // Date picker popover state for mobile
  const [collectionDatePickerOpen, setCollectionDatePickerOpen] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId, selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch ALL pending rents/commissions and payment history for selected month
      // Note: Backend automatically calculates accruals before returning data
      const [rentsRes, paymentsRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/pending-rents`, {
          credentials: "include"
        }),
        // Get ONLY rent + commission debt payments (exclude salary payments)
        fetch(`${API}/businesses/${businessId}/debt-payments?debt_type=rent_commission&month=${selectedMonth}&year=${selectedYear}`, {
          credentials: "include"
        })
      ]);

      if (rentsRes.ok) {
        const data = await rentsRes.json();
        setPendingRents(data);
      }

      if (paymentsRes.ok) {
        const data = await paymentsRes.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("common:toast_verilerYuklenirkenHataOlustu"));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAccruals = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/calculate-rent-accruals`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || t("common:rentDebtsUpdated"));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const openPaymentModal = (accrual) => {
    setSelectedAccrual(accrual);
    setPaymentForm({
      amount: accrual.remaining_amount?.toString() || "",
      payment_method: "cash",
      payment_date: todayLocalString(),
      notes: ""
    });
    setShowPaymentModal(true);
  };

  const openIgnoreModal = (accrual) => {
    setSelectedAccrual(accrual);
    setIgnoreReason("");
    setShowIgnoreModal(true);
  };

  const handleCreatePayment = async () => {
    if (!selectedAccrual || !paymentForm.amount) {
      toast.error(t("payments:amountRequired"));
      return;
    }

    const amount = parseTurkishNumber(paymentForm.amount);
    if (amount <= 0) {
      toast.error(t("common:toast_tutar0danBuyukOlmali"));
      return;
    }
    if (amount > selectedAccrual.remaining_amount) {
      toast.error(`${currencySymbol}${selectedAccrual.remaining_amount.toLocaleString()}`);
      return;
    }

    try {
      // Check if this is a commission debt (from trainer_debts) or rent debt (from accrued_debts)
      const isCommissionDebt = selectedAccrual.debt_type === 'commission';
      
      let response;
      if (isCommissionDebt) {
        // Use trainer-debts endpoint for commission payments
        response = await fetch(`${API}/trainer-debts/${selectedAccrual.debt_id}/pay`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            amount: amount,
            payment_date: paymentForm.payment_date,
            payment_method: paymentForm.payment_method,
            notes: paymentForm.notes
          })
        });
      } else {
        // Use accrued-debts endpoint for rent payments
        response = await fetch(`${API}/accrued-debts/${selectedAccrual.accrual_id}/pay`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            amount: amount,
            payment_date: paymentForm.payment_date,
            payment_method: paymentForm.payment_method,
            notes: paymentForm.notes
          })
        });
      }

      if (response.ok) {
        toast.success(isCommissionDebt ? t("reports:commissionCollectionSaved") : t("common:rentCollectionSaved"));
        setShowPaymentModal(false);
        setSelectedAccrual(null);
        setPaymentForm({ amount: "", payment_method: "cash", payment_date: todayLocalString(), notes: "" });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:collectionSaveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleIgnoreAccrual = async () => {
    if (!selectedAccrual) return;

    try {
      const response = await fetch(`${API}/accrued-debts/${selectedAccrual.accrual_id}/ignore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: ignoreReason })
      });

      if (response.ok) {
        toast.success(t("common:toast_kiraBorcuYoksayildi"));
        setShowIgnoreModal(false);
        setSelectedAccrual(null);
        setIgnoreReason("");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: t("common:cash"),
      credit_card: t("admin:creditCard"),
      bank_transfer: t("payments:bankTransfer"),
      eft: "EFT"
    };
    return labels[method] || method;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-orange-100 text-orange-700 text-xs">{t("common:pendingBadge")}</Badge>;
      case "partial":
        return <Badge className="bg-blue-100 text-blue-700 text-xs">{t("common:partiallyPaid")}</Badge>;
      case "paid":
        return <Badge className="bg-green-100 text-green-700 text-xs">{t('payments:paid')}</Badge>;
      case "ignored":
        return <Badge className="bg-gray-100 text-gray-700 text-xs">{t("common:ignoredBadge")}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredPendingRents = pendingRents.pending_rents?.filter(r => {
    const name = r.entity_name || r.trainer_name || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  const totalPaidThisMonth = paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy">{t('reports:incomingRentCommission')}</h1>
          <p className="text-grey text-sm">{t("reports:antrenorlerdenTahsilEdilenKiraVe")}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefreshAccruals} 
            className="rounded-xl gap-2"
            data-testid="refresh-rent-accruals-btn"
          >
            <RefreshCw className="w-4 h-4" />
            {t("common:update")}
          </Button>
        </div>
      </div>

      {/* Summary Cards - First Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="rounded-2xl border-0 shadow-card bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-10 h-10 text-amber-600" />
              <div>
                <p className="text-sm text-grey">{t("common:pendingRent")}</p>
                <p className="text-2xl font-bold text-amber-700">
                  {currencySymbol}{(pendingRents.total_rent || 0).toLocaleString('tr-TR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-2xl border-0 shadow-card bg-gradient-to-br from-purple-50 to-violet-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-10 h-10 text-purple-600" />
              <div>
                <p className="text-sm text-grey">{t("reports:pendingCommission")}</p>
                <p className="text-2xl font-bold text-purple-700">
                  {currencySymbol}{(pendingRents.total_commission || 0).toLocaleString('tr-TR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-2xl border-0 shadow-card bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Clock className="w-10 h-10 text-blue-600" />
              <div>
                <p className="text-sm text-grey">{t("common:totalPending")}</p>
                <p className="text-2xl font-bold text-blue-700">
                  {currencySymbol}{(pendingRents.total_pending || 0).toLocaleString('tr-TR')}
                </p>
                <p className="text-xs text-blue-500 mt-1">
                  {filteredPendingRents.length} {t("common:pendingDebtText")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-2xl border-0 shadow-card bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Wallet className="w-10 h-10 text-green-600" />
              <div>
                <p className="text-sm text-grey">{t("common:thisMonthCollection")}</p>
                <p className="text-2xl font-bold text-green-700">
                  +{currencySymbol}{totalPaidThisMonth.toLocaleString('tr-TR')}
                </p>
                <p className="text-xs text-green-500 mt-1">
                  {paymentHistory.length} {t('common:paymentText')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Financial Summary Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-0 shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              {t("common:monthlySummary")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
              <span className="text-sm text-amber-700">{t("common:rentToCollect")}</span>
              <span className="font-bold text-amber-700">{currencySymbol}{(pendingRents.total_rent || 0).toLocaleString('tr-TR')}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <span className="text-sm text-purple-700">{t("reports:commissionToCollect")}</span>
              <span className="font-bold text-purple-700">{currencySymbol}{(pendingRents.total_commission || 0).toLocaleString('tr-TR')}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-100 rounded-lg border-2 border-blue-200">
              <span className="font-medium text-blue-800">{t("common:totalToCollect")}</span>
              <span className="font-bold text-xl text-blue-800">{currencySymbol}{(pendingRents.total_pending || 0).toLocaleString('tr-TR')}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-2xl border-0 shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-navy flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              {t("common:thisMonthCollections")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-sm text-green-700">{t("common:collectedRent")}</span>
              <span className="font-bold text-green-700">+{currencySymbol}{paymentHistory.filter(p => p.type === 'rent' || p.debt_type === 'rent').reduce((s, p) => s + (p.amount || 0), 0).toLocaleString('tr-TR')}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-sm text-green-700">{t("reports:collectedCommission")}</span>
              <span className="font-bold text-green-700">+{currencySymbol}{paymentHistory.filter(p => p.type === 'commission' || p.debt_type === 'commission').reduce((s, p) => s + (p.amount || 0), 0).toLocaleString('tr-TR')}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg border-2 border-green-200">
              <span className="font-medium text-green-800">{t("common:totalCollection")}</span>
              <span className="font-bold text-xl text-green-800">+{currencySymbol}{totalPaidThisMonth.toLocaleString('tr-TR')}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 rounded-xl p-1">
          <TabsTrigger value="pending" className="rounded-lg" data-testid="pending-rent-tab">
            <Clock className="w-4 h-4 mr-2" />
            {t("common:pendingDebts")}
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg" data-testid="history-rent-tab">
            <Check className="w-4 h-4 mr-2" />
            {t("common:collectionHistory")}
          </TabsTrigger>
        </TabsList>

        {/* Pending Rents Tab */}
        <TabsContent value="pending" className="mt-4 space-y-4">
          {/* Month Navigation & Search */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2 bg-white rounded-xl border p-2">
              <Button variant="ghost" size="sm" onClick={handlePreviousMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 min-w-[140px] justify-center">
                <Calendar className="w-4 h-4 text-navy" />
                <span className="font-medium text-navy">
                  {getMonthNames()[selectedMonth - 1]} {selectedYear}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleNextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-grey" />
              <Input
                placeholder={t('trainers:searchTrainer')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-xl"
                data-testid="search-rent-input"
              />
            </div>
          </div>

          {/* Pending Rents Table */}
          <Card className="rounded-2xl border-0 shadow-card">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common:trainer')}</TableHead>
                    <TableHead>{t("common:period")}</TableHead>
                    <TableHead>{t("common:typeLabel")}</TableHead>
                    <TableHead>{t("common:source")}</TableHead>
                    <TableHead className="text-right">{t('common:amount')}</TableHead>
                    <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                    <TableHead className="text-right">{t('packages:remaining')}</TableHead>
                    <TableHead>{t('common:status')}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPendingRents.map((rent) => (
                    <TableRow key={rent.accrual_id || rent.debt_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className={`${rent.debt_type === 'commission' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'} text-xs`}>
                              {(rent.entity_name || rent.trainer_name)?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{rent.entity_name || rent.trainer_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getMonthNames()[(rent.month || 1) - 1]} {rent.year}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {rent.debt_type === 'commission' ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">{t("common:commissionBadge")}</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">{t('trainers:rent')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {rent.debt_type === 'commission' ? (
                          <div className="text-xs">
                            {rent.package_name && <div className="font-medium text-gray-700">{rent.package_name}</div>}
                            {rent.member_name && <div className="text-gray-500">{t("common:memberLabel")} {rent.member_name}</div>}
                            {rent.lesson_date && <div className="text-gray-400">{rent.lesson_date} {rent.lesson_time}</div>}
                            {!rent.package_name && !rent.member_name && <span className="text-gray-400 italic">{t("common:disUyeDersi")}</span>}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">{rent.description || t("common:monthlyRentLabel")}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {currencySymbol}{(rent.amount || 0).toLocaleString('tr-TR')}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {currencySymbol}{(rent.paid_amount || 0).toLocaleString('tr-TR')}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${rent.debt_type === 'commission' ? 'text-purple-600' : 'text-amber-600'}`}>
                        {currencySymbol}{(rent.remaining_amount || 0).toLocaleString('tr-TR')}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(rent.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 rounded-lg"
                            onClick={() => openPaymentModal(rent)}
                            data-testid={`collect-rent-${rent.accrual_id || rent.debt_id}`}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {t("common:collectShort")}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openIgnoreModal(rent)}>
                                <X className="w-4 h-4 mr-2" />
                                {t("common:ignoreAction")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredPendingRents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-grey">
                        {t("common:noPendingRentDebt")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history" className="mt-4 space-y-4">
          {/* Month Navigation */}
          <div className="flex items-center gap-2 bg-white rounded-xl border p-2 w-fit">
            <Button variant="ghost" size="sm" onClick={handlePreviousMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <Calendar className="w-4 h-4 text-navy" />
              <span className="font-medium text-navy">
                {getMonthNames()[selectedMonth - 1]} {selectedYear}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleNextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* History Table */}
          <Card className="rounded-2xl border-0 shadow-card">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common:date')}</TableHead>
                    <TableHead>{t('common:trainer')}</TableHead>
                    <TableHead>{t("common:typeLabel")}</TableHead>
                    <TableHead>{t("common:period")}</TableHead>
                    <TableHead>{t("common:method")}</TableHead>
                    <TableHead className="text-right">{t('common:amount')}</TableHead>
                    <TableHead>{t('common:note')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentHistory.map((payment) => (
                    <TableRow key={payment.payment_id}>
                      <TableCell className="text-sm">
                        {payment.payment_date ? format(new Date(payment.payment_date), "dd MMM yyyy", { locale: getDateLocale() }) : "-"}
                      </TableCell>
                      <TableCell className="font-medium">
                        {payment.entity_name || payment.trainer_name}
                      </TableCell>
                      <TableCell>
                        {payment.debt_type === 'commission' ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">{t("common:commissionBadge")}</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">{t('trainers:rent')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {getMonthNames()[(payment.month || 1) - 1]} {payment.year}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getPaymentMethodLabel(payment.payment_method)}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${payment.debt_type === 'commission' ? 'text-purple-600' : 'text-green-600'}`}>
                        +{currencySymbol}{(payment.amount || 0).toLocaleString('tr-TR')}
                      </TableCell>
                      <TableCell className="text-grey text-sm max-w-[150px] truncate">
                        {payment.notes || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {paymentHistory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-grey">
                        {t("common:noCollectionRecord")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-600" />
              {t("common:saveRentCollection")}
            </DialogTitle>
            <DialogDescription>
              {t("common:recordRentCollection")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {selectedAccrual && (
              <div className="p-3 bg-amber-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-amber-100 text-amber-700">
                      {selectedAccrual.entity_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedAccrual.entity_name}</p>
                    <p className="text-sm text-grey">
                      {getMonthNames()[(selectedAccrual.month || 1) - 1]} {selectedAccrual.year} - {t("packages:remaining")}: {currencySymbol}{(selectedAccrual.remaining_amount || 0).toLocaleString('tr-TR')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("common:tahsilatTutari")}</Label>
              <CurrencyInput
                value={paymentForm.amount}
                onChange={(val) => setPaymentForm({...paymentForm, amount: val})}
                placeholder="0"
                className="rounded-xl"
                data-testid="rent-payment-amount"
              />
              <p className="text-xs text-grey">
                {t("common:remainingDebtLabel")}: {currencySymbol}{(selectedAccrual?.remaining_amount || 0).toLocaleString('tr-TR')}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t("common:collectionDateLabel")}</Label>
              <Popover open={collectionDatePickerOpen} onOpenChange={setCollectionDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal rounded-xl"
                    data-testid="rent-payment-date"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {paymentForm.payment_date ? (
                      format(new Date(paymentForm.payment_date), "dd MMMM yyyy", { locale: getDateLocale() })
                    ) : (
                      <span className="text-grey">{t('common:selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={paymentForm.payment_date ? new Date(paymentForm.payment_date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const dateStr = format(date, "yyyy-MM-dd");
                        setPaymentForm({...paymentForm, payment_date: dateStr});
                        setCollectionDatePickerOpen(false);
                      }
                    }}
                    locale={tr}
                    initialFocus={false}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>{t('payments:paymentMethod')}</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}
              >
                <SelectTrigger className="rounded-xl" data-testid="rent-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t("payments:bankTransfer")}</SelectItem>
                  <SelectItem value="eft">{t("payments:eft")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("common:noteOptional")}</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder={t("common:collectionNotePlaceholder")}
                rows={2}
                className="rounded-xl"
                data-testid="rent-payment-notes"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              {t("common:cancel")}
            </Button>
            <Button 
              onClick={handleCreatePayment} 
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-rent-payment-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {t("common:saveCollection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ignore Modal */}
      <Dialog open={showIgnoreModal} onOpenChange={setShowIgnoreModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              {t("common:ignoreRentDebtTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedAccrual && (
              <div className="p-3 bg-orange-50 rounded-xl">
                <p className="text-sm text-orange-700">
                  <strong>{selectedAccrual.entity_name}</strong> {t("common:forEntity")}{" "}
                  <strong>{getMonthNames()[(selectedAccrual.month || 1) - 1]} {selectedAccrual.year}</strong> {t("common:ignoreRentInfo")}{" "}
                  <strong>{currencySymbol}{(selectedAccrual.remaining_amount || 0).toLocaleString('tr-TR')}</strong> {t("common:ignoreRentAmountSuffix")}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("common:ignoreReasonOptional")}</Label>
              <Textarea
                value={ignoreReason}
                onChange={(e) => setIgnoreReason(e.target.value)}
                placeholder={t("common:ph_nedenYoksayildiginiNotEdin")}
                rows={3}
                data-testid="rent-ignore-reason"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowIgnoreModal(false)}>
              {t("common:cancel")}
            </Button>
            <Button 
              onClick={handleIgnoreAccrual} 
              variant="destructive"
              data-testid="confirm-rent-ignore-btn"
            >
              <X className="w-4 h-4 mr-2" />
              {t("common:ignoreAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
